import { AUTH_MODE, cookieRequest } from '@/services/api';

export interface AlbumData {
  id: string;
  title: string;
  description: string;
  coverImage: string;
  photoCount: number;
  createdAt: string;
  updatedAt: string;
  captureDate?: string;
  createdBy?: string;
  creatorName?: string;
}

export interface AlbumInput {
  title: string;
  description: string;
  coverImage: string;
}

export type AlbumUpdateInput = Partial<
  Pick<AlbumData, 'title' | 'description' | 'coverImage' | 'photoCount'>
>;

const STORAGE_KEY = 'bongnas_albums';

const ALBUM_TITLE_COLLATOR = new Intl.Collator(['ko-KR', 'en-US'], {
  numeric: true,
  sensitivity: 'base',
  ignorePunctuation: false,
});

function sortAlbumsByTitle(albums: AlbumData[]): AlbumData[] {
  return [...albums].sort((a, b) => {
    const titleCompare = ALBUM_TITLE_COLLATOR.compare(a.title.trim(), b.title.trim());
    if (titleCompare !== 0) return titleCompare;
    return ALBUM_TITLE_COLLATOR.compare(a.id, b.id);
  });
}

export const COVER_PRESETS = [
  {
    id: 'cover-1',
    image: 'https://readdy.ai/api/search-image?query=Warm%20cozy%20family%20gathering%20in%20bright%20living%20room%20with%20soft%20natural%20window%20light%20streaming%20through%20curtains%2C%20cream%20colored%20sofa%20with%20family%20members%20sitting%20together%20sharing%20laughter%2C%20happy%20expressions%20and%20genuine%20smiles%2C%20beige%20and%20cream%20neutral%20color%20palette%2C%20editorial%20family%20lifestyle%20photography%20with%20soft%20highlights%20and%20clean%20composition%2C%20simple%20minimal%20interior%20background&width=800&height=600&seq=album-cover-preset-01&orientation=landscape',
  },
  {
    id: 'cover-2',
    image: 'https://readdy.ai/api/search-image?query=Outdoor%20family%20picnic%20in%20sunny%20green%20park%20with%20blooming%20flowers%20and%20soft%20grass%2C%20family%20sitting%20on%20cream%20colored%20picnic%20blanket%20sharing%20food%2C%20children%20running%20and%20playing%20in%20background%2C%20warm%20spring%20sunlight%20filtering%20through%20trees%2C%20beige%20and%20soft%20natural%20green%20palette%2C%20joyful%20lifestyle%20photography%20with%20candid%20moments%2C%20simple%20clean%20background&width=800&height=600&seq=album-cover-preset-02&orientation=landscape',
  },
  {
    id: 'cover-3',
    image: 'https://readdy.ai/api/search-image?query=Family%20walking%20together%20on%20sandy%20beach%20at%20golden%20sunset%20with%20gentle%20ocean%20waves%2C%20parents%20holding%20children%20hands%2C%20warm%20amber%20and%20golden%20hour%20backlight%20creating%20silhouettes%2C%20cream%20and%20soft%20coral%20color%20palette%2C%20emotional%20family%20photography%20with%20backlight%20glow%2C%20simple%20clean%20composition%20with%20ocean%20horizon&width=800&height=600&seq=album-cover-preset-03&orientation=landscape',
  },
  {
    id: 'cover-4',
    image: 'https://readdy.ai/api/search-image?query=Celebration%20moment%20with%20family%20around%20dining%20table%20with%20cake%20and%20candles%2C%20warm%20intimate%20indoor%20lighting%20with%20candle%20glow%2C%20hands%20clapping%20and%20smiling%20faces%2C%20cream%20and%20warm%20beige%20color%20palette%2C%20festive%20family%20photography%20with%20cozy%20atmosphere%20and%20genuine%20expressions%2C%20simple%20clean%20background&width=800&height=600&seq=album-cover-preset-04&orientation=landscape',
  },
  {
    id: 'cover-5',
    image: 'https://readdy.ai/api/search-image?query=Family%20hiking%20together%20on%20gentle%20mountain%20trail%20with%20autumn%20colored%20trees%2C%20parents%20backpack%20with%20toddler%20in%20carrier%2C%20golden%20afternoon%20light%20through%20leaves%2C%20warm%20beige%20and%20earthy%20orange%20color%20palette%2C%20adventure%20lifestyle%20photography%20with%20natural%20candid%20poses%2C%20simple%20clean%20composition%20with%20forest%20backdrop&width=800&height=600&seq=album-cover-preset-05&orientation=landscape',
  },
  {
    id: 'cover-6',
    image: 'https://readdy.ai/api/search-image?query=Cozy%20family%20reading%20books%20together%20on%20soft%20rug%20in%20sunlit%20corner%20of%20room%2C%20warm%20floor%20lamp%20glow%2C%20children%20leaning%20on%20parents%2C%20cream%20and%20warm%20amber%20color%20palette%2C%20intimate%20lifestyle%20photography%20with%20soft%20focus%20and%20peaceful%20atmosphere%2C%20simple%20clean%20interior%20background%20with%20bookshelves&width=800&height=600&seq=album-cover-preset-06&orientation=landscape',
  },
];

interface ServerAlbum {
  id: number | string;
  title: string;
  description?: string | null;
  coverImage?: string | null;
  coverPhotoId?: number | string | null;
  photoCount?: number;
  createdBy?: number | string | null;
  creatorName?: string | null;
  createdAt: string;
  updatedAt: string;
  captureDate?: string | null;
}

interface AlbumListResponse {
  albums: ServerAlbum[];
}

interface AlbumResponse {
  album: ServerAlbum;
  message?: string;
}

function formatDate(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.replaceAll('-', '.').slice(0, 10);

  const parts = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  return `${year}.${month}.${day}`;
}

function fallbackCover(id: number | string): string {
  const numericId = Number.parseInt(String(id), 10);
  const index = Number.isFinite(numericId) ? Math.abs(numericId - 1) % COVER_PRESETS.length : 0;
  return COVER_PRESETS[index].image;
}

function mapServerAlbum(album: ServerAlbum): AlbumData {
  return {
    id: String(album.id),
    title: album.title,
    description: album.description ?? '',
    coverImage: album.coverImage || fallbackCover(album.id),
    photoCount: album.photoCount ?? 0,
    createdAt: formatDate(album.createdAt),
    updatedAt: formatDate(album.updatedAt),
    captureDate: album.captureDate ? formatDate(album.captureDate) : undefined,
    createdBy: album.createdBy == null ? undefined : String(album.createdBy),
    creatorName: album.creatorName ?? undefined,
  };
}

function getSeedAlbums(): AlbumData[] {
  return [
    {
      id: '1',
      title: '2025 겨울 가족 여행',
      description: '눈 내리는 겨울, 온 가족이 함께한 따뜻한 여행',
      coverImage: COVER_PRESETS[0].image,
      photoCount: 3,
      createdAt: '2025.12.25',
      updatedAt: '2025.12.28',
    },
    {
      id: '2',
      title: '지호의 첫 돌잔치',
      description: '우리 아기 지호의 특별한 첫 번째 생일',
      coverImage: COVER_PRESETS[3].image,
      photoCount: 3,
      createdAt: '2026.03.15',
      updatedAt: '2026.04.02',
    },
    {
      id: '3',
      title: '할머니 댁 방문',
      description: '오랜만에 찾아뵌 할머니 댁, 가족의 정이 가득한 하루',
      coverImage: COVER_PRESETS[5].image,
      photoCount: 2,
      createdAt: '2026.05.08',
      updatedAt: '2026.05.09',
    },
    {
      id: '4',
      title: '주말 나들이 모음',
      description: '봄바람 따라 떠난 주말 나들이의 행복한 순간들',
      coverImage: COVER_PRESETS[1].image,
      photoCount: 2,
      createdAt: '2026.04.20',
      updatedAt: '2026.05.03',
    },
    {
      id: '5',
      title: '수아의 졸업식',
      description: '우리 딸 수아의 빛나는 졸업식, 모두가 함께 축하한 날',
      coverImage: COVER_PRESETS[3].image,
      photoCount: 2,
      createdAt: '2026.02.28',
      updatedAt: '2026.03.01',
    },
    {
      id: '6',
      title: '여름 휴가 in 제주',
      description: '제주도의 푸른 바다와 함께한 잊지 못할 여름휴가',
      coverImage: COVER_PRESETS[2].image,
      photoCount: 3,
      createdAt: '2025.08.15',
      updatedAt: '2025.08.20',
    },
  ];
}

function readStorage(): AlbumData[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // 손상된 데이터는 초기값으로 복구
  }
  return [];
}

function writeStorage(albums: AlbumData[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(albums));
}

function ensureInit(): AlbumData[] {
  let albums = readStorage();
  if (albums.length === 0) {
    albums = getSeedAlbums();
    writeStorage(albums);
  }
  return albums;
}

export async function getAllAlbums(): Promise<AlbumData[]> {
  if (AUTH_MODE === 'mock') {
    return sortAlbumsByTitle(ensureInit());
  }

  const response = await cookieRequest<AlbumListResponse>('/api/albums');
  return sortAlbumsByTitle(response.albums.map(mapServerAlbum));
}

export async function getAlbumById(id: string): Promise<AlbumData | undefined> {
  if (AUTH_MODE === 'mock') {
    return ensureInit().find((album) => album.id === id);
  }

  try {
    const response = await cookieRequest<AlbumResponse>(`/api/albums/${encodeURIComponent(id)}`);
    return mapServerAlbum(response.album);
  } catch (error) {
    if (error instanceof Error && 'status' in error && error.status === 404) return undefined;
    throw error;
  }
}

export async function createAlbum(data: AlbumInput): Promise<AlbumData> {
  if (AUTH_MODE === 'mock') {
    const albums = ensureInit();
    const now = new Date();
    const dateStr = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')}`;
    const maxId = albums.reduce((max, album) => Math.max(max, Number.parseInt(album.id, 10) || 0), 0);

    const newAlbum: AlbumData = {
      id: String(maxId + 1),
      title: data.title.trim(),
      description: data.description.trim(),
      coverImage: data.coverImage,
      photoCount: 0,
      createdAt: dateStr,
      updatedAt: dateStr,
    };

    albums.push(newAlbum);
    writeStorage(albums);
    return newAlbum;
  }

  const response = await cookieRequest<AlbumResponse>('/api/albums', {
    method: 'POST',
    body: JSON.stringify({
      title: data.title.trim(),
      description: data.description.trim(),
      coverImage: data.coverImage,
    }),
  });
  return mapServerAlbum(response.album);
}

export async function updateAlbum(id: string, data: AlbumUpdateInput): Promise<AlbumData | undefined> {
  if (AUTH_MODE === 'mock') {
    const albums = ensureInit();
    const index = albums.findIndex((album) => album.id === id);
    if (index === -1) return undefined;

    const now = new Date();
    const dateStr = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')}`;
    albums[index] = { ...albums[index], ...data, updatedAt: dateStr };
    writeStorage(albums);
    return albums[index];
  }

  const payload: Record<string, string> = {};
  if (data.title !== undefined) payload.title = data.title.trim();
  if (data.description !== undefined) payload.description = data.description.trim();
  if (data.coverImage !== undefined) payload.coverImage = data.coverImage;

  // 사진 API가 만들어지기 전까지 photoCount만 변경하는 호출은 서버에 보내지 않습니다.
  if (Object.keys(payload).length === 0) return getAlbumById(id);

  const response = await cookieRequest<AlbumResponse>(`/api/albums/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return mapServerAlbum(response.album);
}

export async function deleteAlbum(id: string): Promise<boolean> {
  if (AUTH_MODE === 'mock') {
    const albums = ensureInit();
    const filtered = albums.filter((album) => album.id !== id);
    if (filtered.length === albums.length) return false;
    writeStorage(filtered);
    return true;
  }

  await cookieRequest(`/api/albums/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return true;
}

export async function getPopularAlbums(count: number): Promise<AlbumData[]> {
  const albums = await getAllAlbums();
  return [...albums].sort((a, b) => b.photoCount - a.photoCount).slice(0, count);
}
